import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Package, Box as BoxIcon, Boxes, Car, ArrowRight, Anchor, Calculator, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const ITEM_TYPES = [
    { id: "box",    label: "Box",           Icon: BoxIcon, desc: "Standard cardboard" },
    { id: "barrel", label: "Barrel",        Icon: Anchor,  desc: "55-gal / 100 lb flat" },
    { id: "crate",  label: "Crate",         Icon: Boxes,   desc: "Bulky low-density" },
    { id: "part",   label: "Vehicle Parts", Icon: Car,     desc: "Handling + hazmat screen" },
];

const ORIGINS = [
    { id: "MIA", label: "Miami, FL" },
    { id: "NYC", label: "New York, NY" },
    { id: "TOR", label: "Toronto, ON" },
    { id: "LON", label: "London, UK" },
    { id: "KIN", label: "Kingston, JM" },
    { id: "MBJ", label: "Montego Bay, JM" },
    { id: "OCJ", label: "Ocho Rios, JM" },
];
const DESTINATIONS = [
    { id: "KIN", label: "Kingston" },
    { id: "MBJ", label: "Montego Bay" },
    { id: "OCJ", label: "Ocho Rios" },
    { id: "PMR", label: "Portmore" },
    { id: "MDV", label: "Mandeville" },
];

export default function ShippingPage() {
    const [tab, setTab] = useState("calculator");   // calculator | signup

    return (
        <div className="min-h-screen bg-background" data-testid="shipping-page">
            <Header />
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-white/5">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-2 mb-4">
                            <Package size={12} className="gold-text" strokeWidth={1.5} />
                            <span className="overline">SMART Shipping &amp; Logistics</span>
                        </div>
                        <h1 className="font-serif text-5xl sm:text-6xl tracking-tighter leading-[0.95]">
                            Ship anything, <br />
                            <span className="italic gold-grad">anywhere</span> in Jamaica.
                        </h1>
                        <p className="mt-6 text-muted-foreground max-w-lg leading-relaxed">
                            Boxes, barrels, crates and vehicle parts — from Miami, New York, Toronto
                            and London, delivered right to your door. Instant quotes, transparent pricing,
                            no surprises at customs.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <button
                                data-testid="shipping-tab-calculator"
                                onClick={() => {
                                    setTab("calculator");
                                    setTimeout(() => {
                                        document
                                            .querySelector('[data-testid="shipping-calculator"]')
                                            ?.scrollIntoView({ behavior: "smooth", block: "start" });
                                    }, 60);
                                }}
                                className={`shipping-cta-hero group relative overflow-hidden ${
                                    tab === "calculator" ? "shipping-cta-active" : ""
                                }`}
                            >
                                <span className="shipping-cta-sheen" aria-hidden="true" />
                                <Calculator size={20} strokeWidth={2} className="shrink-0" />
                                <span className="shipping-cta-copy">
                                    <span className="shipping-cta-title">Price Calculator</span>
                                    <span className="shipping-cta-sub">Get an instant quote</span>
                                </span>
                                <ArrowRight size={18} strokeWidth={2} className="shipping-cta-arrow" />
                            </button>
                            <button
                                data-testid="shipping-tab-signup"
                                onClick={() => setTab("signup")}
                                className={`px-5 py-3 rounded-sm text-xs uppercase tracking-[0.2em] border transition-all ${
                                    tab === "signup" ? "bg-primary text-primary-foreground border-primary" : "bg-transparent border-white/15 text-muted-foreground hover:border-white/40"
                                }`}
                            >
                                <ShieldCheck size={12} className="inline mr-2" strokeWidth={1.5} />
                                Open a Shipping Account
                            </button>
                        </div>
                    </div>
                    <div className="lg:col-span-5 relative">
                        <div className="p-6 rounded-md bg-card border border-white/10 h-full">
                            <div className="overline mb-4">Rate card</div>
                            <ul className="space-y-3 text-sm">
                                <RateRow label="Box" price="$60 / lb" />
                                <RateRow label="Barrel (55 gal)" price="$18,000 flat + $80/lb over 100lb" />
                                <RateRow label="Crate" price="$95 / lb" />
                                <RateRow label="Vehicle Part" price="$110/lb + $2,500 handling" />
                                <li className="pt-3 mt-3 border-t border-white/10 text-xs text-muted-foreground">
                                    Domestic Kingston ↔ MoBay ↔ Ocho Rios: flat <strong className="gold-text">$3,500</strong>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
                {tab === "calculator" ? <ShippingCalculator /> : <ShippingSignup onDone={() => setTab("calculator")} />}
            </section>

            <Footer />
        </div>
    );
}

function RateRow({ label, price }) {
    return (
        <li className="flex justify-between gap-3">
            <span className="text-muted-foreground">{label}</span>
            <span className="text-foreground font-medium text-right">{price}</span>
        </li>
    );
}

// ---------------------------------------------------------------------------
// Calculator
// ---------------------------------------------------------------------------
function ShippingCalculator() {
    const [form, setForm] = useState({
        item_type: "box",
        origin: "MIA",
        destination: "KIN",
        weight_lb: "",
        length_in: "",
        width_in: "",
        height_in: "",
        declared_value_jmd: "",
        door_delivery: false,
        fragile_handling: false,
        priority_air: false,
    });
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(false);

    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // Debounced auto-quote whenever form changes and weight is set.
    useEffect(() => {
        const w = parseFloat(form.weight_lb) || 0;
        if (w <= 0) {
            setQuote(null);
            return;
        }
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await api.post("/shipping/quote", {
                    ...form,
                    weight_lb: w,
                    length_in: parseFloat(form.length_in) || 0,
                    width_in: parseFloat(form.width_in) || 0,
                    height_in: parseFloat(form.height_in) || 0,
                    declared_value_jmd: parseFloat(form.declared_value_jmd) || 0,
                });
                setQuote(data);
            } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            } finally {
                setLoading(false);
            }
        }, 450);
        return () => clearTimeout(t);
    }, [form]);

    return (
        <div className="grid lg:grid-cols-12 gap-10" data-testid="shipping-calculator">
            {/* Form */}
            <div className="lg:col-span-7 space-y-6">
                <div>
                    <div className="overline mb-4">Item type</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="shipping-item-types">
                        {ITEM_TYPES.map(({ id, label, Icon, desc }) => {
                            const active = form.item_type === id;
                            return (
                                <button
                                    key={id}
                                    type="button"
                                    onClick={() => upd("item_type", id)}
                                    data-testid={`shipping-item-${id}`}
                                    className={`text-left p-4 rounded-sm border transition-all ${
                                        active
                                            ? "border-primary bg-primary/10"
                                            : "border-white/10 hover:border-white/30"
                                    }`}
                                >
                                    <Icon size={20} className={active ? "gold-text" : "text-muted-foreground"} strokeWidth={1.5} />
                                    <div className={`mt-3 text-sm font-medium ${active ? "gold-text" : "text-foreground"}`}>{label}</div>
                                    <div className="text-[10px] text-muted-foreground mt-0.5 uppercase tracking-[0.15em]">{desc}</div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label className="overline mb-2 block">Origin</Label>
                        <Select value={form.origin} onValueChange={(v) => upd("origin", v)}>
                            <SelectTrigger className="bg-input border-white/10 h-11 rounded-sm" data-testid="shipping-origin">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {ORIGINS.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="overline mb-2 block">Destination</Label>
                        <Select value={form.destination} onValueChange={(v) => upd("destination", v)}>
                            <SelectTrigger className="bg-input border-white/10 h-11 rounded-sm" data-testid="shipping-destination">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {DESTINATIONS.map((d) => <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="grid sm:grid-cols-4 gap-4">
                    <NumField label="Weight (lb) *" value={form.weight_lb} onChange={(v) => upd("weight_lb", v)} testid="shipping-weight" />
                    <NumField label="Length (in)" value={form.length_in} onChange={(v) => upd("length_in", v)} testid="shipping-length" />
                    <NumField label="Width (in)" value={form.width_in} onChange={(v) => upd("width_in", v)} testid="shipping-width" />
                    <NumField label="Height (in)" value={form.height_in} onChange={(v) => upd("height_in", v)} testid="shipping-height" />
                </div>

                <div>
                    <NumField
                        label="Declared value (JMD) — for insurance"
                        value={form.declared_value_jmd}
                        onChange={(v) => upd("declared_value_jmd", v)}
                        testid="shipping-declared-value"
                    />
                </div>

                <div className="space-y-3">
                    <div className="overline">Options</div>
                    <ToggleRow
                        checked={form.door_delivery}
                        onChange={(v) => upd("door_delivery", v)}
                        label="Door-to-door delivery"
                        price="+$2,500"
                        testid="shipping-opt-door"
                    />
                    <ToggleRow
                        checked={form.fragile_handling}
                        onChange={(v) => upd("fragile_handling", v)}
                        label="Fragile handling &amp; extra padding"
                        price="+$1,800"
                        testid="shipping-opt-fragile"
                    />
                    <ToggleRow
                        checked={form.priority_air}
                        onChange={(v) => upd("priority_air", v)}
                        label="Priority Air (skip the boat)"
                        price="+40% surcharge"
                        testid="shipping-opt-priority"
                    />
                </div>
            </div>

            {/* Quote card */}
            <aside className="lg:col-span-5">
                <div className="sticky top-4 p-6 rounded-md bg-card border border-white/10" data-testid="shipping-quote-card">
                    <div className="overline mb-4">Instant quote</div>
                    {!quote && (
                        <div className="text-sm text-muted-foreground py-10 text-center" data-testid="shipping-quote-empty">
                            Enter a weight to see your price.
                        </div>
                    )}
                    {loading && (
                        <div className="text-sm text-muted-foreground py-2">Calculating...</div>
                    )}
                    {quote && (
                        <>
                            <ul className="space-y-2 text-sm" data-testid="shipping-line-items">
                                {quote.line_items.map((li) => (
                                    <li key={`ship-${li.label}`} className="flex justify-between gap-4 text-muted-foreground">
                                        <span>{li.label}</span>
                                        <span className="text-foreground tabular-nums">${li.amount.toLocaleString()}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="hairline my-4" />
                            <div className="flex items-end justify-between">
                                <div>
                                    <div className="overline">Total (JMD)</div>
                                    <div className="font-serif text-4xl mt-1" data-testid="shipping-total">
                                        <span className="gold-text">$</span>
                                        {quote.total_jmd.toLocaleString()}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-4 text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                                Billable {quote.billable_weight_lb} lb · Dim wt {quote.dim_weight_lb} lb
                            </div>
                            <Button
                                className="w-full mt-6 bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-11"
                                onClick={() => toast.info("Sign in / open an account to book this shipment.")}
                                data-testid="shipping-book-cta"
                            >
                                Book this shipment <ArrowRight size={13} className="ml-2" strokeWidth={1.5} />
                            </Button>
                        </>
                    )}
                </div>
            </aside>
        </div>
    );
}

function NumField({ label, value, onChange, testid }) {
    return (
        <div>
            <Label className="overline mb-2 block">{label}</Label>
            <Input
                type="number"
                min="0"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="bg-input border-white/10 h-11 rounded-sm"
                data-testid={testid}
            />
        </div>
    );
}

function ToggleRow({ checked, onChange, label, price, testid }) {
    return (
        <label className="flex items-center gap-3 p-3 border border-white/10 rounded-sm cursor-pointer hover:border-white/30">
            <Checkbox checked={checked} onCheckedChange={(c) => onChange(Boolean(c))} className="border-white/20" data-testid={testid} />
            <div className="flex-1 text-sm">{label}</div>
            <div className="text-xs gold-text tabular-nums">{price}</div>
        </label>
    );
}

// ---------------------------------------------------------------------------
// Signup
// ---------------------------------------------------------------------------
function ShippingSignup({ onDone }) {
    const [form, setForm] = useState({
        name: "", email: "", phone: "", address: "", city: "Kingston", parish: "", password: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (form.password.length < 6) return toast.error("Password must be at least 6 characters.");
        setSubmitting(true);
        try {
            await api.post("/shipping/signup", form);
            toast.success("Account created. Redirecting to the calculator to price your first shipment.");
            setTimeout(onDone, 900);
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} className="max-w-2xl mx-auto space-y-5" data-testid="shipping-signup-form">
            <div>
                <div className="overline mb-2">Open your Shipping Account</div>
                <h2 className="font-serif text-3xl">Sign up in 30 seconds.</h2>
                <p className="text-sm text-muted-foreground mt-2">
                    Track shipments, save recurring routes, view invoices — one dashboard.
                </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <TxtField label="Full name" v={form.name} on={(v) => upd("name", v)} testid="ship-signup-name" />
                <TxtField label="Email" type="email" v={form.email} on={(v) => upd("email", v)} testid="ship-signup-email" />
                <TxtField label="Phone" v={form.phone} on={(v) => upd("phone", v)} testid="ship-signup-phone" />
                <TxtField label="City" v={form.city} on={(v) => upd("city", v)} testid="ship-signup-city" />
            </div>
            <div>
                <Label className="overline mb-2 block">Delivery address in Jamaica</Label>
                <Textarea
                    value={form.address}
                    onChange={(e) => upd("address", e.target.value)}
                    rows={2}
                    className="bg-input border-white/10 rounded-sm"
                    data-testid="ship-signup-address"
                    required
                />
            </div>
            <TxtField label="Password" type="password" v={form.password} on={(v) => upd("password", v)} testid="ship-signup-password" />
            <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-12"
                data-testid="ship-signup-submit"
            >
                {submitting ? "Creating..." : "Create Shipping Account"} <ArrowRight size={14} className="ml-2" strokeWidth={1.5} />
            </Button>
        </form>
    );
}

function TxtField({ label, v, on, testid, type = "text" }) {
    return (
        <div>
            <Label className="overline mb-2 block">{label}</Label>
            <Input
                type={type}
                value={v}
                onChange={(e) => on(e.target.value)}
                className="bg-input border-white/10 h-11 rounded-sm"
                data-testid={testid}
                required
            />
        </div>
    );
}
